import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, CalendarDays, Stethoscope, Users, UserRound, BarChart3, Settings, LogOut, ExternalLink, AlertTriangle, CreditCard, ShieldAlert, Landmark } from "lucide-react";
import { Logo } from "@/components/site/Layout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const LINKS = [
  ["overview", "", "Overview", LayoutDashboard],
  ["appointments", "appointments", "Appointments", CalendarDays],
  ["services", "services", "Treatments", Stethoscope],
  ["practitioners", "practitioners", "Practitioners", UserRound],
  ["patients", "patients", "Patients", Users],
  ["reports", "reports", "Reports", BarChart3],
  ["billing", "billing", "Plan & billing", CreditCard],
  ["payouts", "payouts", "Payments & Stripe", Landmark],
  ["settings", "settings", "Clinic settings", Settings],
];

export const PageHead = ({ eyebrow, title, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <div>
      <p className="eyebrow">{eyebrow}</p>
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-1" data-testid="admin-page-title">{title}</h1>
    </div>
    <div className="flex gap-2">{children}</div>
  </div>
);

export default function AdminLayout() {
  const { user, logout, stopImpersonation } = useAuth();
  const nav = useNavigate();
  const [clinic, setClinic] = useState(null);
  useEffect(() => {
    const load = () => api.get("/admin/clinic").then((r) => setClinic(r.data));
    load();
    window.addEventListener("clinic-updated", load);
    return () => window.removeEventListener("clinic-updated", load);
  }, []);

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-border bg-card sticky top-0 h-screen">
        <div className="h-16 flex items-center px-5 border-b border-border"><Logo to="/admin" /></div>
        <div className="px-5 py-4">
          <p className="text-xs text-muted-foreground">Clinic</p>
          <p className="font-display font-bold text-sm truncate" data-testid="admin-clinic-name">{clinic?.name}</p>
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {LINKS.map(([key, path, label, I]) => (
            <NavLink key={key} to={`/admin/${path}`} end={path === ""} data-testid={`admin-sidebar-link-${key}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-primary text-white" : "text-slate-600 hover:bg-muted"}`}>
              <I className="h-4 w-4" />{label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border space-y-1">
          {clinic?.listed && (
            <Link to={`/clinic/${clinic.slug}`} target="_blank" className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-muted" data-testid="admin-view-public-page-link">
              <ExternalLink className="h-4 w-4" />View booking page
            </Link>
          )}
          <button onClick={async () => { await logout(); nav("/"); }} className="w-full flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-muted" data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" />Log out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        {user?.impersonated_by && (
          <div className="bg-accent text-white px-4 sm:px-8 py-2.5 text-sm flex flex-wrap items-center justify-between gap-2" data-testid="impersonation-banner">
            <span className="flex items-center gap-2"><ShieldAlert className="h-4 w-4" />You're logged in as {user.email} ({clinic?.name})</span>
            <button className="rounded-full bg-white/20 hover:bg-white/30 px-4 py-1 font-medium" data-testid="stop-impersonation-button"
              onClick={async () => { await stopImpersonation(); }}>Return to platform admin</button>
          </div>
        )}
        <header className="h-16 sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border flex items-center justify-between px-4 sm:px-8">
          <div className="md:hidden"><Logo to="/admin" /></div>
          <p className="hidden md:block text-sm text-muted-foreground">Signed in as <span className="text-foreground font-medium">{user?.email}</span></p>
        </header>
        <nav className="md:hidden flex gap-1 overflow-x-auto px-4 py-2 border-b border-border bg-card">
          {LINKS.map(([key, path, label]) => (
            <NavLink key={key} to={`/admin/${path}`} end={path === ""} data-testid={`admin-mobile-link-${key}`}
              className={({ isActive }) => `shrink-0 rounded-full px-3 py-1.5 text-xs font-medium ${isActive ? "bg-primary text-white" : "text-slate-600"}`}>{label}</NavLink>
          ))}
        </nav>
        {clinic && !clinic.approved && (
          <div className="mx-4 sm:mx-8 mt-6 rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 px-5 py-3 text-sm flex items-center gap-2" data-testid="admin-pending-approval-banner">
            <AlertTriangle className="h-4 w-4" /> Your clinic is awaiting approval. Add treatments and practitioners now — it goes live once approved and on an active plan.
          </div>
        )}
        {clinic && clinic.approved && !clinic.listed && (
          <div className="mx-4 sm:mx-8 mt-6 rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 px-5 py-3 text-sm flex flex-wrap items-center gap-2" data-testid="admin-no-plan-banner">
            <AlertTriangle className="h-4 w-4" /> Your clinic is hidden from patients until it has {[
              !(clinic.plan_id && ["active", "trialing", "comp"].includes(clinic.subscription_status)) && "an active plan",
              !(clinic.connect_status === "active" || clinic.sample) && "a connected Stripe account",
            ].filter(Boolean).join(" and ") || "treatments and practitioners"}.
            {!(clinic.connect_status === "active" || clinic.sample) && <Link to="/admin/payouts" className="underline font-medium" data-testid="admin-connect-stripe-link">Connect Stripe</Link>}
            {!(clinic.plan_id && ["active", "trialing", "comp"].includes(clinic.subscription_status)) && <Link to="/admin/billing" className="underline font-medium" data-testid="admin-no-plan-link">Choose a plan</Link>}
          </div>
        )}
        <main className="px-4 sm:px-8 py-8"><Outlet /></main>
      </div>
    </div>
  );
}
